import type { Schluessel, Wert } from '../../platform/einstellungen';
import type { QrVorschau } from '../relayEinstellungenQr';

/**
 * Der Einstellungscode des Classic-Servers, übersetzt in unseren Katalog.
 *
 * ⚠️ **Zweitrangig und gekapselt** (Entscheidung des Nutzers, 2026-10-08): Der Hauptweg ist
 * Relays Code (`relayEinstellungenQr.ts`). Die Classic-Schlüssel kennt nur diese Datei; heraus
 * kommt dieselbe `QrVorschau` in Relays Namen.
 *
 * **Woher das Format kommt.** Der Dialog „Client-Einstellungen" des Classic-Servers schreibt
 * `'settings-' + Ext.JSON.encode(source)` — ein flaches Objekt aus 41 Schlüsseln, dazu ein
 * `i18n`-Feld für Textüberschreibungen. Gelesen wird er von `domain/qrNutzlast.ts`; hier
 * steht nur die Übersetzung.
 *
 * ⚠️ **Nichts erfinden.** Für die Mehrzahl der Classic-Schalter gibt es bei uns keine
 * Entsprechung — sie beschreiben eine Oberfläche, die anders gebaut ist (`horizontal-rating`,
 * `enable-presenter`). Sie werden **genannt**, nicht stillschweigend fallengelassen: der Prüfer
 * soll vor dem Übernehmen sehen, was von seinem Code ankommt und was nicht.
 *
 * ⬅️ **`min-brightness` stand bis zum 2026-09-17 in dieser Aufzählung.** Seit die App eine
 * Helligkeitsspanne führt, hat er eine Entsprechung (`min_brightness`).
 *
 * ⚠️ **Fremde Werte werden geprüft, nicht geglaubt** — dieselbe Regel wie bei den
 * MDM-Werten (`platform/einstellungen.ts`). Ein `'ja'` in einem Schalter landet unter den
 * ignorierten, statt die App in einen Zustand zu bringen, den keine Oberfläche erzeugen kann.
 */

type Uebersetzer = (roh: unknown) => Wert | null;

const text: Uebersetzer = (roh) => (typeof roh === 'string' && roh !== '' ? roh : null);
const schalter: Uebersetzer = (roh) => (typeof roh === 'boolean' ? roh : null);
const sprache: Uebersetzer = (roh) =>
  typeof roh === 'string' && ['de', 'en', 'fr'].includes(roh) ? roh : null;

/**
 * Eine Prozentzahl aus dem Classic-Einstellungscode.
 *
 * ⚠️ **Hier wird nicht beschnitten.** Ob 40 in die Spanne passt, entscheidet die Prüfregel des
 * Katalogs (`platform/einstellungen.ts`, Typ `schieber`) — an einer Stelle, nicht an zweien.
 * Hier fällt nur durch, was gar keine Zahl ist.
 */
const prozent: Uebersetzer = (roh) => {
  const n = typeof roh === 'number' ? roh : typeof roh === 'string' ? Number(roh) : Number.NaN;
  return Number.isFinite(n) ? Math.round(n) : null;
};

/**
 * Classic-Schlüssel → unser Schlüssel. Was hier fehlt, hat keine Entsprechung.
 *
 * `host`/`username`/`password` sind der Grund, warum dieser Code überhaupt gescannt wird: ein
 * frisches iPad ist damit in einem Zug eingerichtet.
 */
const ABBILDUNG: Partial<Record<string, { ziel: Schluessel; lies: Uebersetzer }>> = {
  host: { ziel: 'server_url', lies: text },
  username: { ziel: 'classic_login', lies: text },
  password: { ziel: 'classic_password', lies: text },
  'password-for-settings': { ziel: 'settings_password', lies: text },
  language: { ziel: 'language', lies: sprache },
  'half-points': { ziel: 'sheet_half_points', lies: schalter },
  'show-stop-watch': { ziel: 'sheet_show_timer', lies: schalter },
  'upload-all-photos': { ziel: 'save_photos_in_album', lies: schalter },
  /*
   * ⚠️ Ein **Maximum** kennt der Classic-Server nicht — `max_brightness` bleibt bei der Vorgabe 100.
   * Wer mit einem Classic-Code einrichtet, bekommt also die Untergrenze aus dem Code und die
   * Obergrenze aus dem Katalog.
   */
  'min-brightness': { ziel: 'min_brightness', lies: prozent },
};

/**
 * ⚠️ **Der Modus steht nicht im Code**, lässt sich aber ableiten: ein Code aus dem
 * Classic-Backend kann nur ein Classic-Gerät meinen. Ohne diese Zeile stünde die App nach dem
 * Scannen auf Relay und redete mit einem Server, der sie nicht versteht.
 */
const MODUS_AUS_HERKUNFT: [Schluessel, Wert] = ['server_protocol', 'classic'];

export function ausClassicEinstellungen(werte: Record<string, unknown>): QrVorschau {
  const uebernommen: Partial<Record<Schluessel, Wert>> = {};
  const ignoriert: string[] = [];
  const unbekannt: string[] = [];

  for (const [name, roh] of Object.entries(werte)) {
    const regel = ABBILDUNG[name];
    if (!regel) {
      ignoriert.push(name);
      unbekannt.push(name);
      continue;
    }
    const wert = regel.lies(roh);
    if (wert === null) ignoriert.push(name);
    else uebernommen[regel.ziel] = wert;
  }

  if (Object.keys(uebernommen).length > 0) {
    const [schluessel, wert] = MODUS_AUS_HERKUNFT;
    uebernommen[schluessel] = wert;
  }

  return { herkunft: 'classic', gruppe: null, uebernommen, ignoriert: ignoriert.sort(), unbekannt: unbekannt.sort() };
}
